/**
 * Filename manipulation utilities.
 * Handles filename parsing, basename extraction, and extension replacement.
 *
 * @module filename-utils
 */

import { DropboxFile } from '../../models/dropbox-file';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { ConvertFromAudio, ConvertFromImage, ConvertFromVideo } from '../../types/capability.types';
import { getExtension } from './extension-utils';

/**
 * Replace the extension of a filename.
 * @param fileName - The original filename
 * @param extension - The new extension (with or without leading dot)
 * @returns Filename with new extension
 */
export function replaceExtension(fileName: string, extension: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
        // No extension, just append
        return extension.startsWith('.') ? `${fileName}${extension}` : `${fileName}.${extension}`;
    }

    const fileNameWithoutExtension = fileName.substring(0, lastDotIndex);
    return extension.startsWith('.')
        ? `${fileNameWithoutExtension}${extension}`
        : `${fileNameWithoutExtension}.${extension}`;
}

/**
 * Get the filename without extension (basename).
 * @param filePath - The file path or filename
 * @returns Filename without extension
 *
 * @example
 * getBasename('video.mp4') // 'video'
 * getBasename('my.file.name.txt') // 'my.file.name'
 * getBasename('README') // 'README'
 */
export function getBasename(filePath: string): string {
    const fileName = getFilenameFromPath(filePath);
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === 0) {
        // No extension or hidden file (starts with dot)
        return fileName;
    }

    return fileName.substring(0, lastDotIndex);
}

/**
 * Get just the filename from a full path.
 * @param filePath - The full file path
 * @returns Just the filename portion
 *
 * @example
 * getFilenameFromPath('/path/to/file.txt') // 'file.txt'
 * getFilenameFromPath('file.txt') // 'file.txt'
 */
export function getFilenameFromPath(filePath: string): string {
    const lastSlashIndex = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
    return lastSlashIndex === -1 ? filePath : filePath.substring(lastSlashIndex + 1);
}

/**
 * Get the filename without extension (legacy method name).
 * @deprecated Use getBasename instead
 */
export function getFilename(filePath: string): string {
    const ext = getExtension(filePath);
    // Handle case where extension equals the entire filename (no dot)
    if (filePath === ext) {
        return filePath;
    }
    return filePath.replace(new RegExp(`\\.${escapeRegex(ext)}$`), '');
}

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Get the MIME type for a file.
 * @param file - The file object (File, GoogleDriveFile, or DropboxFile)
 * @returns The MIME type string
 */
export function getFileMimeType(file: File | GoogleDriveFile | DropboxFile): string {
    if (file.type && file.type.length > 0) {
        return file.type;
    }
    return getFileMimeTypeFromFilename(file.name);
}

/**
 * Infer MIME type from filename extension.
 * @param filename - The filename
 * @returns Inferred MIME type
 */
export function getFileMimeTypeFromFilename(filename: string): string {
    const extension = (getExtension(filename) || '').toLowerCase();

    if (ConvertFromImage.EXTENTIONS.includes(extension)) {
        return `image/${extension}`;
    }
    if (ConvertFromVideo.EXTENTIONS.includes(extension)) {
        return `video/${extension}`;
    }
    if (ConvertFromAudio.EXTENTIONS.includes(extension)) {
        return `audio/${extension}`;
    }

    return extension;
}

/**
 * Sanitize a filename by removing invalid characters.
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
    // Remove characters that are invalid in most file systems
    return filename.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_');
}

/**
 * Truncate a filename to a maximum length while preserving extension.
 * @param filename - The filename to truncate
 * @param maxLength - Maximum total length
 * @returns Truncated filename
 */
export function truncateFilename(filename: string, maxLength: number): string {
    if (filename.length <= maxLength) {
        return filename;
    }

    const ext = getExtension(filename);
    const baseName = getBasename(filename);
    const extWithDot = ext ? `.${ext}` : '';

    const maxBaseLength = maxLength - extWithDot.length - 3; // 3 for '...'
    if (maxBaseLength <= 0) {
        return filename.substring(0, maxLength);
    }

    return `${baseName.substring(0, maxBaseLength)}...${extWithDot}`;
}
